export interface TimeSlot {
  day: number; // 1-7 (Monday-Sunday)
  startTime: string; // "HH:MM" format
  endTime: string; // "HH:MM" format
}

export interface ScheduleData {
  slots: TimeSlot[];
}

export interface ScheduleGridProps {
  value?: TimeSlot[];
  onChange?: (slots: TimeSlot[]) => void;
  disabled?: boolean;
}

export interface DaySchedule {
  day: number;
  dayName: string;
  slots: TimeSlot[];
}

// Utility functions
export function timeToMinutes(time: string): number {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
}

export function minutesToTime(minutes: number): string {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
}

export function formatTimeSlot(slot: TimeSlot): string {
  return `${slot.startTime} - ${slot.endTime}`;
}

export function mergeAdjacentSlots(slots: TimeSlot[]): TimeSlot[] {
  if (slots.length === 0) return [];
  
  // Sort slots by day and start time
  const sortedSlots = slots.toSorted((a, b) => {
    if (a.day !== b.day) return a.day - b.day;
    return timeToMinutes(a.startTime) - timeToMinutes(b.startTime);
  });
  
  const merged: TimeSlot[] = [];
  let current = { ...sortedSlots[0] };
  
  for (let i = 1; i < sortedSlots.length; i++) {
    const next = sortedSlots[i];
    
    // If same day and adjacent or overlapping
    if (current.day === next.day && 
        timeToMinutes(current.endTime) >= timeToMinutes(next.startTime)) {
      // Merge slots - extend end time to the later of the two
      current.endTime = timeToMinutes(current.endTime) > timeToMinutes(next.endTime) 
        ? current.endTime 
        : next.endTime;
    } else {
      // Add current slot and start new one
      merged.push(current);
      current = { ...next };
    }
  }
  
  merged.push(current);
  return merged;
}

export function validateTimeSlot(slot: TimeSlot): boolean {
  const start = timeToMinutes(slot.startTime);
  const end = timeToMinutes(slot.endTime);
  
  return start < end && 
         start >= 0 && 
         end <= 24 * 60 && 
         slot.day >= 1 && 
         slot.day <= 7;
}

export function hasOverlap(slots: TimeSlot[], newSlot: TimeSlot): boolean {
  return slots.some(slot => 
    slot.day === newSlot.day &&
    !(timeToMinutes(newSlot.endTime) <= timeToMinutes(slot.startTime) ||
      timeToMinutes(newSlot.startTime) >= timeToMinutes(slot.endTime))
  );
}
